/**
 *
 * Add Ariksa Outpost
 *
 */

import React, { FC, useEffect, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { Screens } from '@ariksa/inventory-core';
import {
  Box,
  Center,
  Flex,
  HStack,
  ListItem,
  Stack,
  UnorderedList,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';
import { customTheme } from 'theme';
import { v4 as uuidv4 } from 'uuid';

import { authOptions } from 'api/auth';
import { VerifyService } from 'api/auth/api.pb';
import { queryStringFromObject } from 'api/utils';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { Form, FormSchema } from 'components/DataEntry/Form';
import {
  CheckmarkCircleIcon,
  CopyIcon,
  errorToast,
  PrimaryButton,
  QuestionCircleIcon,
} from 'components/index';
import { selectUser } from 'containers/App/selectors';
import { selectOutpost } from 'containers/Setup/Outpost/selectors';
import { OutpostProps } from 'containers/Setup/Outpost/types';
import { selectSetup } from 'containers/Setup/selectors';
import { actions as setupActions } from 'containers/Setup/slice';
import { useScannerInfo } from 'containers/SharedState/hooks/useScannerInfo';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

import { formStyles } from '../../../../CloudAccounts/CloudAccountWizard/styles';

export const AddAriksaOutpostForVulnerabilityForm: FC<OutpostProps> = props => {
  const {
    accountOptions,
    selectedAccount,
    regionOptions,
    selectedTab,
    selectedRegion,
    selectedZone,
    zoneOptions,
    network,
    subnet,
    virtualNetworkResourceGroup,
    updateValue,
  } = props;
  const navigate = useNavigate();
  const { host } = useLocation();
  const [sharedSecret, setSharedSecret] = useState('');
  const { info } = useSelector(selectUser);
  const { outpostToken, zones } = useSelector(selectOutpost);
  const { configCommands, outpostConfig } = useSelector(selectSetup);
  const { regions } = useSelector(selectSharedState);
  const [isCopied, setIsCopied] = useState(false);
  const dispatch = useDispatch();
  const { onSuccessVulnerabilityScanner } = useScannerInfo();

  useEffect(() => {
    const interval = setInterval(() => {
      setIsCopied(false);
    }, 9000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!!sharedSecret) {
      dispatch(
        setupActions.getConfigCommands({
          q: {
            screen: Screens.VulnerabilityScannerDeployment,
            zone: selectedZone?.value,
            sharedSecret: `${info.org_id}::${
              host?.split('.')[0]
            }::${sharedSecret}`,
            network, //: network?.value,
            subnet, //: subnet?.value,
            virtualNetworkResourceGroup,
            region: selectedRegion?.value,
          },
        }),
      );
    }
  }, [
    selectedZone?.value,
    sharedSecret,
    dispatch,
    info.org_id,
    host,
    virtualNetworkResourceGroup,
    subnet,
    network,
    selectedRegion,
  ]);

  /*//get networks
  useEffect(() => {
    if (
      selectedTab === CloudProviders.Gcp ||
      selectedTab === CloudProviders.Azure
    ) {
      if (!selectedAccount?.value || !selectedRegion?.value) return;
      dispatch(
        actions.getNetworks({
          q: {
            insightV2Request: {
              account_id: [selectedAccount?.value],
              resource_type:
                selectedTab === CloudProviders.Gcp
                  ? NativeResources.GcpVirtualPrivateCloud
                  : NativeResources.AzureVirtualNetwork,
              page: 1,
              size: 10000,
              category: 'Network',
              region: selectedRegion?.value,
            },
          },
          onSuccess: res => {
            const options = map(res?.items, o => ({
              label: o?.name ?? o?.resource_id,
              value: o?.resource_id,
            }));
            updateValue('networkOptions', options);
            updateValue('network', options?.[0]);
          },
        }),
      );
    }
  }, [selectedTab, dispatch, selectedAccount, selectedRegion, updateValue]);

  //get subnets
  useEffect(() => {
    if (
      selectedTab === CloudProviders.Gcp ||
      selectedTab === CloudProviders.Azure
    ) {
      if (!selectedAccount?.value || !selectedRegion?.value || !network?.value)
        return;
      dispatch(
        actions.getSubnets({
          q: {
            insightV2Request: {
              account_id: [selectedAccount?.value],
              resource_type:
                selectedTab === CloudProviders.Gcp
                  ? 'GCPSubnetwork'
                  : NativeResources.AzureVirtualNetworkSubnets,
              page: 1,
              size: 10000,
              category: 'Network',
              region: selectedRegion?.value,
              vpcs: [network?.value],
            },
          },
          onSuccess: res => {
            const options = map(res?.items, o => ({
              label: o?.name ?? o?.resource_id,
              value: o?.resource_id,
            }));
            updateValue('subnetOptions', options);
            updateValue('subnet', options?.[0]);
          },
        }),
      );
    }
  }, [
    selectedTab,
    dispatch,
    selectedAccount,
    selectedRegion,
    network,
    updateValue,
  ]);*/

  const handleGenerateToken = () => {
    VerifyService.GetClientCredentials({}, authOptions())
      .then(res => {
        setSharedSecret(res.client_secret ?? '');
      })
      .catch(err => {
        errorToast({
          title: err.message,
        });
      });
  };

  const handleSubmit = data => {
    const { region } = data;
    if (!sharedSecret) {
      errorToast({ title: 'Outpost token is not generated' });
      return;
    }

    let parameters: Record<string, any> = {
      stackName: `Ariksa-Outpost-Vuln-${
        selectedAccount?.data?.cloud_account_id
      }-${uuidv4()}`,
      templateURL: outpostConfig.data?.cloud_template_map?.[selectedTab],
      //'https://ezl7u4ytyo-s5cwguevl7vglyy8y-hd1s8.s3.amazonaws.com/ariksa-outpost-cloudformation.yaml',
      param_SharedSecret: `${info.org_id}::${
        host?.split('.')[0]
      }::${sharedSecret}`,
    };

    let redirectUrl = `https://${
      region?.value
    }.console.aws.amazon.com/cloudformation/home?region=${
      region?.value
    }#/stacks/quickcreate${queryStringFromObject(parameters, {
      useSnakeCase: false,
    })}`;

    if (selectedTab === CloudProviders.Gcp) {
      redirectUrl = 'https://console.cloud.google.com';
    } else if (selectedTab === CloudProviders.Azure) {
      redirectUrl = 'https://portal.azure.com/#cloudshell/';
    }

    navigate('/setup/outpost');
    window.open(redirectUrl, '_blank');

    dispatch(
      sharedStateActions.getVulnerabilityScannerInfo({
        q: {},
        onSuccess: onSuccessVulnerabilityScanner,
      }),
    );
  };

  const handleReset = () => {
    navigate('/setup/outpost');
  };

  const containerStyles = {
    border: '1px solid',
    borderColor: customTheme.colors.gray['200'],
    py: 1,
    px: 3,
    w: 'full',
    borderRadius: '3px',
    overflow: 'auto',
  };

  const zone: FormSchema =
    selectedTab === CloudProviders.Gcp
      ? {
          zone: {
            type: 'react-select',
            options: zoneOptions,
            value: selectedZone,
            onChange: v => updateValue('selectedZone', v),
            label: 'Zone',
            isLoading: zones.isLoading,
            isDisabled: regions.isLoading || zones.isLoading,
          },
        }
      : {};

  const subnetAndNetwork: FormSchema =
    selectedTab === CloudProviders.Gcp || selectedTab === CloudProviders.Azure
      ? {
          network: {
            type: 'text',
            label: 'Network',
            //options: networkOptions,
            value: network,
            onChange: s => updateValue('network', s),
            //isLoading: networks.isLoading,
            helpIcon: <QuestionCircleIcon />,
            helpTooltip:
              'Which virtual network should Ariksa Outpost be deployed to?',
          },
          subnet: {
            type: 'text',
            label: 'Subnet',
            //options: subnetOptions,
            value: subnet,
            onChange: s => updateValue('subnet', s),
            //isLoading: subnets.isLoading,
            helpIcon: <QuestionCircleIcon />,
            helpTooltip:
              'Which subnet in above VPC should Ariksa Outpost be deployed in? ' +
              'Ensure that Network Access Control List (NACL) allows egress traffic ' +
              'on port 443 to Internet. This is required for Ariksa Outpost to communicate with Ariksa SaaS.',
          },
        }
      : {};

  const resourceGroup: FormSchema =
    selectedTab === CloudProviders.Azure
      ? {
          virtualNetworkResourceGroup: {
            type: 'text',
            label: 'Virtual Network Resource Group',
            onChange: s => updateValue('virtualNetworkResourceGroup', s),
          },
        }
      : {};

  const gcpCommand: FormSchema =
    (selectedTab === CloudProviders.Gcp ||
      selectedTab === CloudProviders.Azure) &&
    !!sharedSecret &&
    !!outpostConfig.data //&&
      ? //!!selectedZone?.value
        {
          gcpCommand: {
            type: 'custom-with-form-control',
            label: 'Copy and paste the following script in Cloudshell',
            component: (
              <Box p={4} borderRadius={6} bg={customTheme.colors.gray['50']}>
                <HStack justify="space-between" align="flex-start" w="full">
                  <Box flex={1}>
                    {configCommands?.isLoading ? (
                      <CustomSpinner />
                    ) : (
                      configCommands.data?.cloud_commands_map?.[selectedTab]
                        ?.vulnerability_scanner_deployment?.deployment
                    )}
                  </Box>
                  <Box
                    boxSize={6}
                    color={
                      isCopied ? customTheme.colors.green['300'] : 'primary'
                    }
                    cursor={isCopied ? 'default' : 'pointer'}
                  >
                    <Center w="full" h="full">
                      <CustomTooltip label="Copy command">
                        {isCopied ? (
                          <CustomTooltip label="Copied to clipboard">
                            <CheckmarkCircleIcon />
                          </CustomTooltip>
                        ) : (
                          <CopyIcon
                            onClick={() => {
                              navigator.clipboard.writeText(
                                configCommands.data?.cloud_commands_map?.[
                                  selectedTab
                                ]?.vulnerability_scanner_deployment?.deployment,
                              );
                              setIsCopied(true);
                            }}
                          />
                        )}
                      </CustomTooltip>
                    </Center>
                  </Box>
                </HStack>
              </Box>
            ),
          },
        }
      : {};

  return (
    <>
      <Form
        schema={{
          account_name: {
            type: 'react-select',
            label: 'Account',
            onChange: s => {
              updateValue('selectedAccount', s);
              updateValue('selectedRegion', {});
              updateValue('selectedZone', {});
            },
            options: accountOptions,
            value: selectedAccount,
          },
          region: {
            type: 'react-select',
            options: regionOptions,
            value: selectedRegion,
            onChange: s => {
              updateValue('selectedRegion', s);
              updateValue('selectedZone', {});
            },
            label: 'Region',
            isLoading: regions.isLoading,
            isDisabled: regions.isLoading,
          },
          ...zone,
          ...subnetAndNetwork,
          ...resourceGroup,
          role_arn_creation: {
            type: 'custom-with-form-control',
            label: 'Authentication Token for Outpost',
            component: (
              <HStack w="full">
                <Box {...containerStyles} h={8} fontSize="22px" flex={1}>
                  {sharedSecret
                    ? '****************************************'
                    : ''}
                </Box>
                <Flex justify="flex-end">
                  <PrimaryButton onClick={handleGenerateToken}>
                    Generate token
                  </PrimaryButton>
                </Flex>
              </HStack>
            ),
          },
          ...gcpCommand,
          note: {
            type: 'custom',
            component: () =>
              selectedTab === CloudProviders.Gcp ? (
                <Stack color="primary">
                  <Box fontWeight={600} fontSize="md">
                    NOTE:
                  </Box>
                  <Box>
                    You will be redirected to GCP console. Copy and paste the
                    script in Cloudshell.
                  </Box>
                </Stack>
              ) : selectedTab === CloudProviders.Azure ? (
                <Stack color="primary">
                  <Box fontWeight={600} fontSize="md">
                    NOTE:
                  </Box>
                  <Box>
                    You will be redirected to Azure portal. Copy and paste the
                    script in Cloudshell.
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={0} color="primary" fontSize="md">
                  <Box fontWeight={600} fontSize="md">
                    NOTE:
                  </Box>
                  <Box>
                    You will be redirected to login to your AWS account to
                    provide inputs required for deploying Ariksa Output for
                    Vulnerability. You’ll need to pick the following:
                  </Box>
                  <Box>
                    <UnorderedList>
                      <ListItem>Virtual Private Cloud (VPC)</ListItem>
                      <ListItem>Subnet</ListItem>
                    </UnorderedList>
                  </Box>
                </Stack>
              ),
          },
        }}
        buttonOptions={{
          submit: {
            name: '+ Add Outpost',
            isLoading: outpostToken.isLoading || configCommands.isLoading,
            isDisabled:
              !sharedSecret ||
              outpostToken.isLoading ||
              configCommands.isLoading ||
              !outpostToken.data ||
              isEmpty(regions.data),
          },
          reset: {
            name: 'Cancel',
            isVisible: true,
            onClick: handleReset,
          },
        }}
        styles={formStyles()}
        handleSubmit={handleSubmit}
      />
    </>
  );
};
